import { List, Typography } from '@mui/material'
import { useMemo } from 'react'
import { MenuListItem } from 'utoniq-ui'

import { BaseLayout, Head } from '@/components/layouts'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Template } from '@/core/architecture'
import {
  individualHideDelivery,
  individualHideTwitterAuth,
} from '@/core/individual'
import { filterHiddenItem } from '@/lib/array'

export * from './fragments'

export type State = {
  isMembershipPersonalInformationRequired: boolean
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {}

type ListProps = {
  title: string
  href: string
  hidden?: boolean
  notificationText?: string
}

export const AccountSettingsTemplate: Template<State, Action> = ({ state }) => {
  const { t } = useTranslation('accountSettings')
  const { routerPages, routerNames } = useRouter()

  const list = useMemo<ListProps[]>(
    () => [
      {
        title: t('emailAddressSetting'),
        href: routerPages.EmailSetting,
      },
      {
        title: t('passwordSetting'),
        href: routerPages.PasswordSetting,
      },
      {
        title: t('personalInfoSetting'),
        href: routerPages.PersonalInfoSettings,
        notificationText: state.isMembershipPersonalInformationRequired
          ? t('personalInfoSettingNotification')
          : '',
      },
      {
        title: t('deliveryAddressSetting'),
        href: routerPages.DeliveryInfoSetting,
        hidden: individualHideDelivery(),
      },
      {
        title: t('bankCreate'),
        href: routerPages.BankCreate,
        hidden: !config.modules.resale,
      },
      {
        title: t('integration'),
        href: routerPages.Integrations,
        hidden: !config.platform.includes('web') || individualHideTwitterAuth(),
      },
      {
        title: t('deactivation'),
        href: routerPages.Deactivation,
      },
    ],
    [
      routerPages.BankCreate,
      routerPages.Deactivation,
      routerPages.DeliveryInfoSetting,
      routerPages.EmailSetting,
      routerPages.Integrations,
      routerPages.PasswordSetting,
      routerPages.PersonalInfoSettings,
      state.isMembershipPersonalInformationRequired,
      t,
    ]
  )

  return (
    <BaseLayout
      header={{
        title: t('title'),
        back: true,
        defaultBackPage: routerPages.Setting,
      }}
    >
      <Head title={routerNames.AccountSetting} />
      <List sx={{ bgcolor: 'grey.50' }}>
        {filterHiddenItem(list).map((item, index) => (
          <MenuListItem
            divider
            key={index}
            title={item.title}
            href={item.href}
            sx={{
              backgroundColor: 'grey.50',
              py: 2,
              px: 5,
              height: { xs: 52, md: 56 },
            }}
            primaryTypographyProps={{
              variant: 'body1',
            }}
            secondaryAction={
              <Typography variant="overline" color="like.tap">
                {item.notificationText}
              </Typography>
            }
          />
        ))}
      </List>
    </BaseLayout>
  )
}
